import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, Home, Camera, Users, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const iconMap = {
  home: Home,
  camera: Camera,
  users: Users
};

// Default config fallback
const DEFAULT_CONFIG = {
  services: [
    {
      id: "immobilien",
      name_de: "Immobilien",
      name_ru: "Недвижимость",
      icon: "home",
      per_photo_price: 10,
      packages: [
        { id: "small", price: 149, photos: 15, label_de: "Wohnung (bis 60m²)", label_ru: "Квартира (до 60м²)" },
        { id: "medium", price: 199, photos: 25, label_de: "Wohnung/Haus (60-120m²)", label_ru: "Квартира/Дом (60-120м²)" },
        { id: "large", price: 299, photos: 40, label_de: "Großes Objekt (120-200m²)", label_ru: "Большой объект (120-200м²)" },
        { id: "xlarge", price: 399, photos: 60, label_de: "Villa/Gewerbe (200m²+)", label_ru: "Вилла/Коммерция (200м²+)" }
      ],
      extras: [
        { id: "drone", price: 99, label_de: "Drohnenaufnahmen", label_ru: "Съёмка с дрона" },
        { id: "twilight", price: 79, label_de: "Dämmerungsfotos", label_ru: "Фото в сумерках" },
        { id: "virtualStaging", price: 49, label_de: "Virtuelles Staging (pro Raum)", label_ru: "Виртуальный стейджинг (за комнату)" },
        { id: "floorPlan", price: 59, label_de: "2D Grundriss", label_ru: "2D планировка" },
        { id: "video", price: 199, label_de: "Video-Tour (60 Sek.)", label_ru: "Видео-тур (60 сек.)" }
      ]
    }
  ]
};

const PriceCalculator = ({ compact = false, onPriceChange = null }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  // Config from API
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [configLoaded, setConfigLoaded] = useState(false);
  
  // Calculator state
  const [serviceType, setServiceType] = useState('immobilien');
  const [selectedPackage, setSelectedPackage] = useState('medium');
  const [photoCount, setPhotoCount] = useState(25);
  const [extras, setExtras] = useState({});
  
  // Calculated price
  const [totalPrice, setTotalPrice] = useState(0);
  const [breakdown, setBreakdown] = useState([]);

  // Load config from API
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch('/api/public/calculator-config');
      const data = await response.json();
      if (data.services && data.services.length > 0) {
        setConfig(data);
        setServiceType(data.services[0].id);
        if (data.services[0].packages?.length > 0) {
          const defaultPkg = data.services[0].packages[1] || data.services[0].packages[0];
          setSelectedPackage(defaultPkg.id);
          setPhotoCount(defaultPkg.photos);
        }
      }
      setConfigLoaded(true);
    } catch (error) {
      console.error('Error loading calculator config:', error);
      setConfigLoaded(true);
    }
  };

  // Get current service
  const currentService = config.services?.find(s => s.id === serviceType) || config.services?.[0];
  const currentPackage = currentService?.packages?.find(p => p.id === selectedPackage);

  // Calculate price when inputs change
  useEffect(() => {
    if (!currentService || !currentPackage) return;
    calculatePrice();
  }, [serviceType, selectedPackage, photoCount, extras, currentService, currentPackage]);

  const calculatePrice = () => {
    if (!currentService || !currentPackage) return;

    let price = currentPackage.price;
    let items = [
      { 
        name: currentLang === 'de' ? currentPackage.label_de : currentPackage.label_ru,
        price: currentPackage.price,
        included: `${currentPackage.photos} ${currentLang === 'de' ? 'Fotos inkl.' : 'фото вкл.'}`
      }
    ];

    // Add extra photos cost
    const extraPhotos = Math.max(0, photoCount - currentPackage.photos);
    if (extraPhotos > 0) {
      const extraCost = extraPhotos * currentService.per_photo_price;
      price += extraCost;
      items.push({
        name: currentLang === 'de' ? `${extraPhotos} zusätzliche Fotos` : `${extraPhotos} дополнительных фото`,
        price: extraCost,
        included: `${currentService.per_photo_price}€ ${currentLang === 'de' ? 'pro Foto' : 'за фото'}`
      });
    }

    // Add extras
    Object.entries(extras).forEach(([key, selected]) => {
      if (selected) {
        const extra = currentService.extras?.find(e => e.id === key);
        if (extra) {
          price += extra.price;
          items.push({
            name: currentLang === 'de' ? extra.label_de : extra.label_ru,
            price: extra.price
          });
        }
      }
    });

    setTotalPrice(price);
    setBreakdown(items);
    
    if (onPriceChange) {
      onPriceChange(price, items);
    }
  };

  const toggleExtra = (key) => {
    setExtras(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleServiceChange = (newServiceId) => {
    setServiceType(newServiceId);
    setExtras({});
    const newService = config.services?.find(s => s.id === newServiceId);
    if (newService?.packages?.length > 0) {
      const defaultPkg = newService.packages[1] || newService.packages[0];
      setSelectedPackage(defaultPkg.id);
      setPhotoCount(defaultPkg.photos);
    }
  };

  const handlePackageChange = (pkgId) => {
    setSelectedPackage(pkgId);
    const pkg = currentService?.packages?.find(p => p.id === pkgId);
    if (pkg) {
      setPhotoCount(pkg.photos);
    }
  };

  if (!configLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  // Compact mode for embedding
  if (compact) {
    return (
      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center gap-2 mb-4">
          <Calculator className="h-5 w-5 text-white" />
          <h3 className="text-lg font-semibold text-white">
            {currentLang === 'de' ? 'Schnellkalkulation' : 'Быстрый расчёт'}
          </h3>
        </div>
        
        {/* Service Selection */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {config.services?.map((service) => {
            const Icon = iconMap[service.icon] || Home;
            return (
              <button
                key={service.id}
                onClick={() => handleServiceChange(service.id)}
                className={`p-3 rounded-lg border transition-all text-center ${
                  serviceType === service.id
                    ? 'border-white bg-white/10'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                <Icon className={`h-5 w-5 mx-auto mb-1 ${serviceType === service.id ? 'text-white' : 'text-gray-400'}`} />
                <span className={`text-xs ${serviceType === service.id ? 'text-white' : 'text-gray-400'}`}>
                  {currentLang === 'de' ? service.name_de : service.name_ru}
                </span>
              </button>
            );
          })}
        </div>

        {/* Package Selection */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {currentService?.packages?.slice(0, 4).map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => handlePackageChange(pkg.id)}
              className={`p-3 rounded-lg border transition-all text-left ${
                selectedPackage === pkg.id
                  ? 'border-white bg-white/10'
                  : 'border-gray-700 hover:border-gray-500'
              }`}
            >
              <div className="text-lg font-bold text-white">€{pkg.price}</div>
              <div className="text-xs text-gray-400">{pkg.photos} {currentLang === 'de' ? 'Fotos' : 'фото'}</div>
            </button>
          ))}
        </div>

        {/* Total & Link */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-700">
          <div>
            <div className="text-sm text-gray-400">{currentLang === 'de' ? 'Ab' : 'От'}</div>
            <div className="text-2xl font-bold text-white">€{totalPrice}</div>
          </div>
          <Link
            to="/calculator"
            className="px-4 py-2 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
          >
            {currentLang === 'de' ? 'Details →' : 'Подробнее →'}
          </Link>
        </div>
      </div>
    );
  }

  // Full calculator
  return (
    <div className="bg-black text-white min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-12">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-black"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
            <Calculator className="h-5 w-5" />
            <span className="text-sm font-medium">
              {currentLang === 'de' ? 'Sofort-Kalkulator' : 'Мгновенный расчёт'}
            </span>
          </div>
          <h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'de' ? 'Preiskalkulator' : 'Калькулятор цен'}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {currentLang === 'de' 
              ? 'Berechnen Sie sofort den Preis für Ihr Fotoprojekt'
              : 'Рассчитайте стоимость вашего фотопроекта мгновенно'
            }
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left Column - Options */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Service Type Selection */}
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h2 className="text-xl font-semibold mb-4">
                  {currentLang === 'de' ? '1. Art der Fotografie' : '1. Тип съёмки'}
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {config.services?.map((service) => {
                    const Icon = iconMap[service.icon] || Home;
                    return (
                      <button
                        key={service.id}
                        onClick={() => handleServiceChange(service.id)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          serviceType === service.id
                            ? 'border-white bg-white/10'
                            : 'border-gray-700 hover:border-gray-500'
                        }`}
                      >
                        <Icon className={`h-8 w-8 mx-auto mb-2 ${serviceType === service.id ? 'text-white' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${serviceType === service.id ? 'text-white' : 'text-gray-300'}`}>
                          {currentLang === 'de' ? service.name_de : service.name_ru}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Package Selection */}
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h2 className="text-xl font-semibold mb-4">
                  {currentLang === 'de' ? '2. Paket wählen' : '2. Выберите пакет'}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {currentService?.packages?.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => handlePackageChange(pkg.id)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedPackage === pkg.id
                          ? 'border-white bg-white/10'
                          : 'border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <div className="text-lg font-bold text-white mb-1">€{pkg.price}</div>
                      <div className="text-xs text-gray-400 mb-2">{pkg.photos} {currentLang === 'de' ? 'Fotos' : 'фото'}</div>
                      <div className={`text-xs ${selectedPackage === pkg.id ? 'text-white' : 'text-gray-300'}`}>
                        {currentLang === 'de' ? pkg.label_de : pkg.label_ru}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Photo Count Slider */}
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h2 className="text-xl font-semibold mb-4">
                  {currentLang === 'de' ? '3. Anzahl der Fotos' : '3. Количество фото'}
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold text-white">{photoCount}</span>
                    <span className="text-sm text-gray-400">
                      {currentPackage && photoCount > currentPackage.photos && (
                        <>+{photoCount - currentPackage.photos} {currentLang === 'de' ? 'extra' : 'доп.'}</>
                      )}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={currentPackage?.photos || 10}
                    max={(currentPackage?.photos || 10) * 3}
                    value={photoCount}
                    onChange={(e) => setPhotoCount(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-white"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{currentPackage?.photos || 10} ({currentLang === 'de' ? 'inkl.' : 'вкл.'})</span>
                    <span>{(currentPackage?.photos || 10) * 3}</span>
                  </div>
                </div>
              </div>

              {/* Extras */}
              {currentService?.extras?.length > 0 && (
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <h2 className="text-xl font-semibold mb-4">
                    {currentLang === 'de' ? '4. Zusatzoptionen' : '4. Дополнительные опции'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {currentService.extras.map((extra) => (
                      <button
                        key={extra.id}
                        onClick={() => toggleExtra(extra.id)}
                        className={`p-4 rounded-lg border-2 transition-all flex items-center justify-between ${
                          extras[extra.id]
                            ? 'border-white bg-white/10'
                            : 'border-gray-700 hover:border-gray-500'
                        }`}
                      >
                        <div className="text-left">
                          <div className={`font-medium ${extras[extra.id] ? 'text-white' : 'text-gray-300'}`}>
                            {currentLang === 'de' ? extra.label_de : extra.label_ru}
                          </div>
                          <div className="text-sm text-gray-500">+€{extra.price}</div>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          extras[extra.id] ? 'border-white bg-white' : 'border-gray-600'
                        }`}>
                          {extras[extra.id] && <Check className="h-4 w-4 text-black" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Price Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h2 className="text-xl font-semibold mb-6">
                  {currentLang === 'de' ? 'Ihre Kalkulation' : 'Ваш расчёт'}
                </h2>
                
                {/* Breakdown */}
                <div className="space-y-3 mb-6">
                  {breakdown.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-start text-sm">
                      <div>
                        <div className="text-gray-300">{item.name}</div>
                        {item.included && (
                          <div className="text-xs text-gray-500">{item.included}</div>
                        )}
                      </div>
                      <div className="text-white font-medium">€{item.price}</div>
                    </div>
                  ))}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-700 my-4"></div>

                {/* Total */}
                <div className="flex justify-between items-center mb-6">
                  <span className="text-lg font-semibold">
                    {currentLang === 'de' ? 'Gesamtpreis' : 'Итого'}
                  </span>
                  <span className="text-3xl font-bold text-white">€{totalPrice}</span>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3">
                  <Link
                    to="/booking"
                    className="w-full flex items-center justify-center gap-2 bg-white text-black py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                  >
                    {currentLang === 'de' ? 'Jetzt buchen' : 'Забронировать'}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/contact"
                    className="w-full block text-center py-3 px-6 border border-gray-600 rounded-lg text-gray-300 hover:border-white hover:text-white transition-colors"
                  >
                    {currentLang === 'de' ? 'Beratung anfragen' : 'Запросить консультацию'}
                  </Link>
                </div>

                {/* Note */}
                <p className="text-xs text-gray-500 mt-4 text-center">
                  {currentLang === 'de' 
                    ? '* Unverbindliche Preiskalkulation. Endpreis kann je nach Projektanforderungen variieren.'
                    : '* Необязывающий расчёт. Итоговая цена может варьироваться в зависимости от требований проекта.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-3xl font-bold text-center mb-12"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'de' ? 'Häufige Fragen' : 'Часто задаваемые вопросы'}
          </h2>
          
          <div className="space-y-6">
            <div className="bg-gray-800/50 rounded-lg p-6">
              <h3 className="font-semibold text-white mb-2">
                {currentLang === 'de' ? 'Was ist im Basispreis enthalten?' : 'Что включено в базовую цену?'}
              </h3>
              <p className="text-gray-400">
                {currentLang === 'de' 
                  ? 'Der Basispreis beinhaltet das Fotoshooting vor Ort, professionelle Bildbearbeitung aller Fotos, und die Bereitstellung der Bilder in hoher Auflösung über eine Online-Galerie.'
                  : 'Базовая цена включает фотосъёмку на месте, профессиональную обработку всех фотографий и предоставление изображений в высоком разрешении через онлайн-галерею.'
                }
              </p>
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-6">
              <h3 className="font-semibold text-white mb-2">
                {currentLang === 'de' ? 'Wie lange dauert die Bearbeitung?' : 'Сколько времени занимает обработка?'}
              </h3>
              <p className="text-gray-400">
                {currentLang === 'de' 
                  ? 'Standardmäßig erhalten Sie Ihre bearbeiteten Fotos innerhalb von 3-5 Werktagen. Express-Bearbeitung innerhalb von 24 Stunden ist gegen Aufpreis möglich.'
                  : 'Стандартно вы получите обработанные фотографии в течение 3-5 рабочих дней. Экспресс-обработка в течение 24 часов доступна за дополнительную плату.'
                }
              </p>
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-6">
              <h3 className="font-semibold text-white mb-2">
                {currentLang === 'de' ? 'Kann ich einzelne Fotos nachbestellen?' : 'Можно ли дозаказать отдельные фото?'}
              </h3>
              <p className="text-gray-400">
                {currentLang === 'de' 
                  ? `Ja, zusätzliche Fotos können jederzeit nachbestellt werden. Der Preis beträgt €${currentService?.per_photo_price || 10} pro zusätzliches Foto.`
                  : `Да, дополнительные фотографии можно заказать в любое время. Стоимость составляет €${currentService?.per_photo_price || 10} за дополнительное фото.`
                }
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PriceCalculator;
